export interface SearchItem {
  id: string;
  title: string;
  description: string;
  category: 'Solutions' | 'Industries' | 'Platform' | 'Resources' | 'Company' | 'Support';
  type: string;
  href: string;
  keywords: string[];
}

export const searchData: SearchItem[] = [
  // Solutions
  {
    id: 'publishers',
    title: 'Publishers',
    description: 'Create interactive ad formats to unlock higher-value inventory and stronger direct revenue.',
    category: 'Solutions',
    type: 'Solution',
    href: '/solutions/publishers',
    keywords: ['publisher', 'inventory', 'CPM', 'ad operations', 'editorial', 'sales', 'premium', 'revenue', 'direct sales'],
  },
  {
    id: 'agencies',
    title: 'Agencies',
    description: 'Creating interactive ads helps agencies deliver stronger results, and more revenue for clients.',
    category: 'Solutions',
    type: 'Solution',
    href: '/solutions/agencies',
    keywords: ['agency', 'creative', 'strategy', 'media', 'client', 'campaign', 'performance', 'production', 'scale'],
  },
  {
    id: 'brands',
    title: 'Brands',
    description: 'Interactive ads help brands stand out, engage audiences, and drive measurable results.',
    category: 'Solutions',
    type: 'Solution',
    href: '/solutions/brands',
    keywords: ['brand', 'marketing', 'awareness', 'engagement', 'conversion', 'performance', 'media investment', 'ROI'],
  },

  // Industries
  {
    id: 'fashion',
    title: 'Fashion',
    description: 'Create interactive ads to turn inspiration into action for fashion consumers.',
    category: 'Industries',
    type: 'Industry',
    href: '/solutions/fashion',
    keywords: ['fashion', 'apparel', 'luxury', 'footwear', 'accessories', 'retail', 'lookbook', 'shoppable', 'style'],
  },
  {
    id: 'travel',
    title: 'Travel & Tourism',
    description: 'Create interactive storytelling ads to drive deeper engagement and more bookings.',
    category: 'Industries',
    type: 'Industry',
    href: '/solutions/travel-and-tourism',
    keywords: ['travel', 'tourism', 'airline', 'hotel', 'destination', 'booking', 'vacation', 'hospitality'],
  },
  {
    id: 'automotive',
    title: 'Automotive',
    description: 'Give shoppers the ability to explore before they ever visit the showroom.',
    category: 'Industries',
    type: 'Industry',
    href: '/solutions/automotive',
    keywords: ['automotive', 'car', 'vehicle', 'OEM', 'EV', 'dealership', 'showroom', 'configurator', 'auto'],
  },
  {
    id: 'entertainment',
    title: 'Entertainment & Media',
    description: 'Turn static promotions into immersive previews that drive real excitement.',
    category: 'Industries',
    type: 'Industry',
    href: '/solutions/entertainment-and-media',
    keywords: ['entertainment', 'media', 'streaming', 'film', 'gaming', 'trailer', 'studio', 'subscriber'],
  },
  {
    id: 'consumer-electronics',
    title: 'Consumer Electronics',
    description: 'Bring product features to life with interactive demos.',
    category: 'Industries',
    type: 'Industry',
    href: '/solutions/consumer-electronics',
    keywords: ['electronics', 'tech', 'mobile', 'audio', 'smart home', 'wearable', 'gadget', 'device', 'product'],
  },
  {
    id: 'financial-services',
    title: 'Financial Services',
    description: 'Make complex financial products easier to understand and easier to convert.',
    category: 'Industries',
    type: 'Industry',
    href: '/solutions/financial-services',
    keywords: ['finance', 'banking', 'insurance', 'credit', 'investment', 'fintech', 'bank', 'loan', 'mortgage'],
  },

  // Platform
  {
    id: 'platform',
    title: 'Platform Overview',
    description: 'Discover the Adventive platform - Interactive Ad Builder, Creative Delivery, and Performance Insights.',
    category: 'Platform',
    type: 'Platform',
    href: '/platform',
    keywords: ['platform', 'builder', 'delivery', 'trafficking', 'insights', 'analytics', 'dashboard', 'tools'],
  },
  {
    id: 'contextiq',
    title: 'ContextIQ',
    description: 'AI-powered contextual intelligence to discover brand partnerships and drive revenue.',
    category: 'Platform',
    type: 'Product',
    href: '/contextiq',
    keywords: ['contextiq', 'context', 'intelligence', 'insight', 'publisher', 'agency', 'revenue', 'content', 'brand alignment', 'monetization', 'sentiment', 'tone', 'ai', 'partnership'],
  },

  {
    id: 'ad-builder',
    title: 'Interactive Ad Builder',
    description: 'Build, customize, and launch high-impact formats in minutes—no code, no heavy production.',
    category: 'Platform',
    type: 'Feature',
    href: '/platform',
    keywords: ['builder', 'create', 'design', 'template', 'no-code', 'drag-drop', 'customize', 'format'],
  },
  {
    id: 'creative-delivery',
    title: 'Creative Delivery & Trafficking',
    description: 'Seamlessly traffic campaigns across platforms and make mid-flight creative updates.',
    category: 'Platform',
    type: 'Feature',
    href: '/platform',
    keywords: ['delivery', 'trafficking', 'ad server', 'campaign', 'distribute', 'publish', 'launch'],
  },
  {
    id: 'performance-insights',
    title: 'Performance Insights',
    description: 'Track engagement, viewability, and outcomes in one place to prove performance.',
    category: 'Platform',
    type: 'Feature',
    href: '/platform',
    keywords: ['analytics', 'insights', 'performance', 'metrics', 'engagement', 'viewability', 'reporting', 'data'],
  },
  {
    id: 'managed-services',
    title: 'Managed Services',
    description: "Let our expert team create high-performance ad units for you. $500 per ad unit.",
    category: 'Platform',
    type: 'Product',
    href: '/managed-services',
    keywords: ['managed', 'services', 'support', 'help', 'team', 'assistance', 'expert', 'consultation', 'done for you', 'creative', 'production'],
  },

  {
    id: 'dashboard-resources',
    title: 'In-Platform Resources',
    description: "Documentation, tutorials, FAQs, and best practices inside the platform.",
    category: 'Platform',
    type: 'Resource',
    href: '/dashboard/resources',
    keywords: ['resources', 'help', 'documentation', 'tutorials', 'guides', 'faq', 'learning'],
  },

  // Resources
  {
    id: 'resources',
    title: 'Resources',
    description: 'Guides, case studies, and webinars to help you master interactive advertising.',
    category: 'Resources',
    type: 'Resource Hub',
    href: '/resources',
    keywords: ['resources', 'learn', 'guide', 'case study', 'webinar', 'education', 'knowledge'],
  },
  {
    id: 'templates',
    title: 'HTML5 Ad Templates',
    description: 'Browse our collection of professionally designed HTML5 ad templates.',
    category: 'Resources',
    type: 'Templates',
    href: '/resources/templates',
    keywords: ['templates', 'html5', 'ad templates', 'gallery', 'creative', 'formats', 'download'],
  },
  {
    id: 'help-center',
    title: 'Help Center',
    description: 'Search our knowledge base or browse categories to find answers.',
    category: 'Resources',
    type: 'Support',
    href: '/resources/help',
    keywords: ['help', 'support', 'knowledge base', 'faq', 'questions', 'documentation', 'articles'],
  },
  {
    id: 'legal',
    title: 'Legal Documents',
    description: 'Review our terms, policies, and legal agreements.',
    category: 'Resources',
    type: 'Legal',
    href: '/resources/legal',
    keywords: ['legal', 'terms', 'privacy', 'policy', 'compliance', 'gdpr', 'dpa'],
  },
  {
    id: 'guide-interactive-ads',
    title: 'The Complete Guide to Interactive Advertising',
    description: 'Everything you need to know about creating high-impact interactive ads that drive results.',
    category: 'Resources',
    type: 'Guide',
    href: '/resources',
    keywords: ['guide', 'interactive', 'advertising', 'tutorial', 'how-to', 'learn', 'best practices'],
  },
  {
    id: 'case-study-publisher',
    title: 'How a Major Publisher Increased CPMs by 3x',
    description: 'Learn how one of our customers transformed their ad inventory with interactive formats.',
    category: 'Resources',
    type: 'Case Study',
    href: '/resources',
    keywords: ['case study', 'publisher', 'CPM', 'success', 'results', 'example', 'story'],
  },
  {
    id: 'webinar-101',
    title: 'Interactive Ads 101: Getting Started',
    description: 'A comprehensive introduction to creating your first interactive ad campaign.',
    category: 'Resources',
    type: 'Webinar',
    href: '/resources',
    keywords: ['webinar', 'video', 'introduction', 'getting started', 'beginner', 'tutorial'],
  },
  {
    id: 'guide-fashion',
    title: 'Best Practices for Fashion Advertising',
    description: 'Industry-specific strategies for creating shoppable lookbooks and interactive catalogs.',
    category: 'Resources',
    type: 'Guide',
    href: '/resources',
    keywords: ['fashion', 'guide', 'lookbook', 'shoppable', 'catalog', 'best practices'],
  },
  {
    id: 'case-study-automotive',
    title: 'Automotive Brand Drives 4x Engagement',
    description: 'See how an automotive brand used interactive configurators to boost campaign performance.',
    category: 'Resources',
    type: 'Case Study',
    href: '/resources',
    keywords: ['automotive', 'case study', 'engagement', 'configurator', 'success'],
  },
  {
    id: 'webinar-performance',
    title: 'Measuring Interactive Ad Performance',
    description: 'Deep dive into the metrics that matter for interactive advertising campaigns.',
    category: 'Resources',
    type: 'Webinar',
    href: '/resources',
    keywords: ['webinar', 'performance', 'metrics', 'measurement', 'analytics', 'KPI'],
  },
  {
    id: 'gallery',
    title: 'Ad Gallery',
    description: 'Explore our collection of high-impact interactive ad examples across industries.',
    category: 'Resources',
    type: 'Gallery',
    href: '/gallery',
    keywords: ['gallery', 'examples', 'showcase', 'portfolio', 'ads', 'creative', 'inspiration'],
  },

  // Company
  {
    id: 'about',
    title: 'About Adventive',
    description: 'Learn about our mission to help publishers, agencies, and brands create high-impact interactive ads.',
    category: 'Company',
    type: 'Company',
    href: '/about',
    keywords: ['about', 'company', 'mission', 'team', 'story', 'who we are', 'history'],
  },
  {
    id: 'careers',
    title: 'Careers',
    description: 'Join our team and help shape the future of interactive advertising.',
    category: 'Company',
    type: 'Company',
    href: '/careers',
    keywords: ['careers', 'jobs', 'hiring', 'work', 'team', 'opportunities', 'employment'],
  },
  {
    id: 'contact',
    title: 'Contact Us',
    description: 'Get in touch with our team for questions, support, or partnership inquiries.',
    category: 'Company',
    type: 'Company',
    href: '/contact',
    keywords: ['contact', 'support', 'help', 'email', 'phone', 'reach out', 'questions'],
  },
  {
    id: 'brand-guidelines',
    title: 'Brand Guidelines',
    description: 'Access our comprehensive brand guidelines including logo, colors, typography, and design principles.',
    category: 'Company',
    type: 'Company',
    href: '/brand-guidelines',
    keywords: ['brand', 'guidelines', 'logo', 'colors', 'typography', 'design', 'style guide', 'identity', 'assets'],
  },

  {
    id: 'partners',
    title: 'Partner Portal',
    description: 'Join our partner ecosystem to unlock new revenue streams, access exclusive resources, and deliver interactive advertising solutions.',
    category: 'Company',
    type: 'Company',
    href: '/partners',
    keywords: ['partners', 'partnership', 'agency', 'technology', 'referral', 'commission', 'co-marketing', 'reseller', 'affiliate'],
  },




  // Support
  {
    id: 'demo',
    title: 'Request a Demo',
    description: 'See Adventive in action. Schedule a personalized demo with our team.',
    category: 'Support',
    type: 'Action',
    href: '/demo',
    keywords: ['demo', 'request', 'trial', 'schedule', 'meeting', 'presentation', 'see it work'],
  },
  {
    id: 'login',
    title: 'Login',
    description: 'Access your Adventive account and dashboard.',
    category: 'Support',
    type: 'Action',
    href: '/login',
    keywords: ['login', 'sign in', 'account', 'dashboard', 'access', 'portal'],
  },
  {
    id: 'privacy',
    title: 'Privacy Policy',
    description: 'Learn how we protect your data and privacy.',
    category: 'Support',
    type: 'Legal',
    href: '/privacy',
    keywords: ['privacy', 'policy', 'data', 'GDPR', 'protection', 'security'],
  },
  {
    id: 'terms',
    title: 'Terms of Service',
    description: 'Review our terms and conditions for using Adventive.',
    category: 'Support',
    type: 'Legal',
    href: '/terms',
    keywords: ['terms', 'service', 'conditions', 'agreement', 'legal'],
  },
];

export const searchCategories = [
  'All',
  'Solutions',
  'Industries',
  'Platform',
  'Resources',
  'Company',
  'Support',
] as const;

export type SearchCategory = typeof searchCategories[number];
